<!--
  ReworkPanel.vue -- card "Digitizer loop" (https://trello.com/c/FKGMWf9Z).
  Design: docs/decisions/2026-09-14-digitizer-rework-flow-and-thread-privacy.md

  The distributor's EDITORIAL GATE between the client's feedback and the
  digitizer. The client rejects a proof with feedback; this panel opens
  prefilled with that feedback verbatim, the distributor edits it, and
  submits. Jake, 2026-09-14: "the default can be verbatim what the client
  requested, but the distributor needs to be able to edit/adjust the
  feedback before submitting the rework."

  The editability is the whole point. A panel that rendered the client's
  words read-only and posted them unchanged would be a relay, and the gate
  would not exist.

  Presentation-only, like its sibling ApprovalThread.vue: the parent owns
  the API call, the capability check and the pending/error state. This
  component owns the draft and emits it.

  Two behaviours worth knowing before editing this file:

  1. THE DRAFT SURVIVES AN ERROR. ``error`` changing does not touch
     ``draft`` -- a 502 from the vendor must not discard an instruction the
     distributor just wrote, because the prefill would not bring it back
     and they would be retyping from memory.

  2. THE DRAFT IS REPLACED WHEN ``prefill`` CHANGES. Selecting a different
     approval record must not leave the previous client's words in the box,
     which would invite submitting one client's feedback against another's
     job. The watcher is deliberately on ``prefill``, not on a record id
     this component does not have.

  Props:
    - prefill:   string | null -- the CLIENT'S latest change request, verbatim
    - canSubmit: boolean       -- a reworkable digitizer job exists
    - pending:   boolean       -- a submit is in flight
    - locked:    boolean       -- sending is off: the last attempt's outcome
                                  is unknown (it may have been billed), so
                                  the parent must not allow a second send
    - error:     string | null -- last failure, shown inline

  Emits:
    - submit(instruction)
-->
<template>
    <div v-if="canSubmit" class="aw-rework" data-testid="rework-panel">
        <div class="aw-rework__header">
            <span class="aw-rework__title">Send back to digitizer</span>
        </div>

        <p class="aw-rework__note" data-testid="rework-privacy-note">
            This instruction is sent to the digitizer and is not shared with
            the client. They see only that the design went back for rework.
        </p>

        <textarea
            v-model="draft"
            rows="4"
            class="aw-rework__textarea"
            placeholder="What should the digitizer change?"
            data-testid="rework-instruction"
            :disabled="pending"
        ></textarea>

        <div class="aw-rework__actions">
            <button
                type="button"
                class="aw-rework__submit"
                :disabled="pending || submitting || locked || !draft.trim()"
                data-testid="rework-submit"
                @click="onSubmit"
            >
                {{ pending || submitting ? 'Sending...' : 'Send rework' }}
            </button>
        </div>

        <p v-if="error" class="aw-rework__error" data-testid="rework-error">
            {{ error }}
        </p>
    </div>
</template>

<script>
export default {
    name: 'ReworkPanel',
    props: {
        prefill: {
            type: String,
            default: null,
        },
        canSubmit: {
            type: Boolean,
            default: false,
        },
        pending: {
            type: Boolean,
            default: false,
        },
        locked: {
            type: Boolean,
            default: false,
        },
        error: {
            type: String,
            default: null,
        },
    },
    emits: ['submit'],
    data() {
        return {
            draft: this.prefill || '',
            // Local in-flight latch. See onSubmit -- this is NOT a
            // duplicate of the ``pending`` prop.
            submitting: false,
        }
    },
    watch: {
        prefill(next) {
            // A different record was selected -- adopt its client feedback.
            // See note 2 in the file header for why this is not guarded on
            // "the user has typed something": keeping a stale draft across a
            // record change is the more dangerous of the two failures.
            this.draft = next || ''
            // A new record means the previous submission is no longer the
            // one in flight from this panel's point of view.
            this.submitting = false
        },
        pending(next) {
            // The parent has finished (success or failure). Release the
            // latch so a retry is possible. Releasing on the parent's
            // signal rather than on a timer means the button re-arms
            // exactly when the request is actually over.
            if (!next) this.submitting = false
        },
    },
    methods: {
        onSubmit() {
            const instruction = this.draft.trim()
            if (!instruction) return

            // WHY A LOCAL LATCH AND NOT JUST THE ``pending`` PROP.
            // ``pending`` is owned by the parent and only becomes true
            // after the parent's handler runs and Vue re-renders. Two
            // clicks landing in the same tick therefore both see
            // ``pending === false`` and both emit -- and each emit is a
            // BILLABLE, non-idempotent vendor call (POST /api/order/edit),
            // which the API refuses with a 409 but which should never be
            // provoked from here in the first place.
            //
            // The latch closes synchronously on the first click, so the
            // second is a no-op regardless of when the parent re-renders.
            if (this.pending || this.submitting || this.locked) return
            this.submitting = true
            this.$emit('submit', instruction)
        },
    },
}
</script>

<style scoped>
.aw-rework {
    margin-top: 1rem;
    padding: 0.75rem;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background: #fafafa;
}

.aw-rework__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.25rem;
}

.aw-rework__title {
    font-weight: 600;
    font-size: 0.9rem;
}

.aw-rework__note {
    margin: 0 0 0.5rem;
    font-size: 0.78rem;
    color: #666;
}

.aw-rework__textarea {
    width: 100%;
    resize: vertical;
    font: inherit;
    padding: 0.5rem;
    border: 1px solid #ccc;
    border-radius: 4px;
}

.aw-rework__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 0.5rem;
}

.aw-rework__submit {
    padding: 0.4rem 0.9rem;
    border-radius: 4px;
    border: 1px solid #1976d2;
    background: #1976d2;
    color: #fff;
    cursor: pointer;
}

.aw-rework__submit:disabled {
    opacity: 0.5;
    cursor: default;
}

.aw-rework__error {
    margin: 0.5rem 0 0;
    font-size: 0.8rem;
    color: #c62828;
}
</style>
